import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import http from "../../../services/http";
import { getVendorSettings, setVendorData } from "../../../services/helper";

const VENDOR_KEY = "addon_custom_options";

const AddonSelector = ({
  addon,
  productId,
  onSelect,
  onDeselect,
  onNext,
  onBack,
  onDone,
  showBack,
  selectedOptions,
  maxQuantity,
  disableAutoAdvance = false,
}) => {
  const [otherModal, setOtherModal] = useState(false);
  const [saveMode, setSaveMode] = useState(false);
  const [otherName, setOtherName] = useState("");
  const [otherPrice, setOtherPrice] = useState("");
  const [savedOptions, setSavedOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const onNextRef = useRef(onNext);
  useEffect(() => { onNextRef.current = onNext; });

  // Track whether the user has interacted with this addon step so the
  // auto-advance doesn't fire immediately when navigating back to a
  // step that is already fully filled.
  const hasInteracted = useRef(false);
  useEffect(() => { hasInteracted.current = false; }, [addon.id]);

  const addonKey = `${productId}_${addon.id}`;

  useEffect(() => {
    const all = getVendorSettings(VENDOR_KEY) || {};
    const options = Array.isArray(all[addonKey]) ? all[addonKey] : [];
    setSavedOptions(options.map((o) => ({ ...o, _isLocal: true })));
  }, [addon.id]);

  const persistOptions = async (updatedRaw) => {
    const all = getVendorSettings(VENDOR_KEY) || {};
    const newAll = { ...all, [addonKey]: updatedRaw };
    setVendorData({ [VENDOR_KEY]: newAll });
    await http.saveVendorData({ [VENDOR_KEY]: newAll });
  };

  const allOptions = [...addon.options, ...savedOptions];
  const currentSelections = selectedOptions[addon.id] || {};
  const totalSelected = Object.values(currentSelections).reduce(
    (sum, qty) => sum + (qty || 0),
    0,
  );

  // Auto-advance when all required flavors/scoops are selected
  useEffect(() => {
    if (!maxQuantity || !hasInteracted.current) return;
    if (totalSelected >= maxQuantity) {
      const timer = setTimeout(() => onNextRef.current(), 300);
      return () => clearTimeout(timer);
    }
  }, [totalSelected, maxQuantity]);

  const handleSelectOption = (option) => {
    const currentQty = currentSelections[option.id] || 0;
    if (maxQuantity && currentQty > 0 && totalSelected >= maxQuantity) {
      onDeselect(addon, option, true);
    } else if (maxQuantity && totalSelected >= maxQuantity) {
      toast.error(`Maximum ${maxQuantity} selections allowed`);
    } else {
      hasInteracted.current = true;
      onSelect(addon, option);
      if (!addon.is_multiple && !disableAutoAdvance) {
        setTimeout(() => onNextRef.current(), 100);
      }
    }
  };

  const handleOtherSubmit = async () => {
    if (!otherName.trim()) return toast.error("Please enter a name");
    const customOption = {
      id: `other_${addon.id}_${Date.now()}`,
      option: otherName.trim(),
      price: otherPrice || "0",
    };

    if (saveMode) {
      // Strip _isLocal before persisting
      const updatedRaw = [
        ...savedOptions.map(({ _isLocal, ...o }) => o),
        customOption,
      ];
      setSavedOptions(updatedRaw.map((o) => ({ ...o, _isLocal: true })));
      setSaving(true);
      try {
        await persistOptions(updatedRaw);
        toast.success(`"${customOption.option}" saved to settings`);
      } catch {
        toast.error("Failed to save to settings. Option active for this session only.");
      } finally {
        setSaving(false);
      }
    }

    onSelect(addon, customOption, true);
    setOtherName("");
    setOtherPrice("");
    setOtherModal(false);
    setSaveMode(false);
    if (!addon.is_multiple && !disableAutoAdvance) {
      setTimeout(() => onNextRef.current(), 100);
    }
  };

  const handleRemoveSavedOption = async (optionId) => {
    const updatedRaw = savedOptions
      .filter((o) => o.id !== optionId)
      .map(({ _isLocal, ...o }) => o);
    setSavedOptions(updatedRaw.map((o) => ({ ...o, _isLocal: true })));
    try {
      await persistOptions(updatedRaw);
    } catch {
      toast.error("Failed to remove option from settings.");
    }
    if (currentSelections[optionId]) {
      const fakeOption = savedOptions.find((o) => o.id === optionId);
      if (fakeOption) onDeselect(addon, fakeOption, true);
    }
  };

  useEffect(() => {
    if (otherModal) return;
    const handleKeyDown = (e) => {
      if (e.key >= "1" && e.key <= "9") {
        const idx = parseInt(e.key) - 1;
        if (idx < allOptions.length) {
          e.preventDefault();
          handleSelectOption(allOptions[idx]);
        } else if (idx === allOptions.length) {
          e.preventDefault();
          setSaveMode(true);
          setOtherModal(true);
        } else if (idx === allOptions.length + 1) {
          e.preventDefault();
          setSaveMode(false);
          setOtherModal(true);
        }
      } else if (e.ctrlKey && e.key === " " && addon.is_multiple) {
        e.preventDefault();
        onNext();
      } else if (e.key === "ArrowRight" && addon.is_multiple) {
        e.preventDefault();
        onNext();
      } else if (e.key === "ArrowLeft" && showBack) {
        e.preventDefault();
        onBack();
      } else if (e.key === "Enter" && showBack) {
        e.preventDefault();
        onDone();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [addon, onNext, onBack, showBack, otherModal]);

  const selectedOptionsList = Object.keys(currentSelections)
    .filter((optId) => currentSelections[optId])
    .map((optId) => {
      const opt = allOptions.find((o) => o.id === optId);
      return opt ? { ...opt, qty: currentSelections[optId] } : null;
    })
    .filter(Boolean);

  return (
    <div className="conversational-addon-container">
      <div className="mb-4">
        <h4 style={{ color: "#fff", fontWeight: "600", marginBottom: "8px" }}>
          {addon.title}
          {addon.is_required && (
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
          )}
        </h4>
        <p
          className="text-muted"
          style={{ fontSize: "14px", marginBottom: "0", color: "#ddd" }}
        >
          Press 1-9 for quick select
          {addon.is_multiple && <span> • Ctrl+Space to continue</span>}
          {showBack && <span> • Enter to done</span>}
          {maxQuantity && (
            <span>
              {" "}
              • {totalSelected}/{maxQuantity} selected
            </span>
          )}
        </p>
      </div>

      {selectedOptionsList.length > 0 && (
        <div className="mb-3">
          <p className="text-muted mb-2" style={{ color: "#aaa" }}>
            Selected:
          </p>
          <div className="d-flex flex-wrap gap-2">
            {selectedOptionsList.map((option) => (
              <Badge key={option.id} color="success" pill>
                {option.option}
                {option.qty > 1 && ` x${option.qty}`}
                {option.price &&
                  ` (+$${(parseFloat(option.price) * option.qty).toFixed(2)})`}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="available-options">
        <div className="d-flex flex-wrap gap-3">
          {[
            ...allOptions,
            {
              id: `save_${addon.id}`,
              option: "Local +",
              price: null,
              _isSave: true,
            },
            {
              id: `other_${addon.id}`,
              option: "Other",
              price: null,
              _isOther: true,
            },
          ].map((option, index) => (
            <div
              key={option.id}
              onClick={() => {
                if (option._isOther) {
                  setSaveMode(false);
                  setOtherModal(true);
                } else if (option._isSave) {
                  setSaveMode(true);
                  setOtherModal(true);
                } else {
                  handleSelectOption(option);
                }
              }}
              style={{
                position: "relative",
                width: "120px",
                cursor: "pointer",
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.05)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
            >
              {option.imageUrl ? (
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    border: currentSelections[option.id]
                      ? "4px solid #28a745"
                      : "2px solid #e0e0e0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    position: "relative",
                    boxShadow: currentSelections[option.id]
                      ? "0 4px 12px rgba(40, 167, 69, 0.3)"
                      : "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <img
                    src={option.imageUrl}
                    alt={option.option}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "6px",
                      left: "6px",
                      background: "rgba(0,0,0,0.8)",
                      color: "white",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    {index + 1}
                  </div>
                  {currentSelections[option.id] && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "rgba(40, 167, 69, 0.9)",
                        color: "white",
                        borderRadius: "50%",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      {currentSelections[option.id] > 1
                        ? currentSelections[option.id]
                        : "✓"}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    border: currentSelections[option.id]
                      ? "4px solid #28a745"
                      : "2px solid #e0e0e0",
                    borderRadius: "12px",
                    position: "relative",
                    background: option._isOther
                      ? "#ffffff"
                      : option._isSave
                        ? "#ffffff"
                        : option._isLocal
                          ? "#2a9d8f"
                          : option.color || "#f8f9fa",
                    boxShadow: currentSelections[option.id]
                      ? "0 4px 12px rgba(40, 167, 69, 0.3)"
                      : "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "6px",
                      left: "6px",
                      background: "rgba(0,0,0,0.8)",
                      color: "white",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    {index + 1}
                  </div>
                  {currentSelections[option.id] && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "rgba(40, 167, 69, 0.9)",
                        color: "white",
                        borderRadius: "50%",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      {currentSelections[option.id] > 1
                        ? currentSelections[option.id]
                        : "✓"}
                    </div>
                  )}
                </div>
              )}
              <div
                style={{
                  marginTop: "8px",
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#fff",
                }}
              >
                {option.option}
                {option._isSave && (
                  <div style={{ fontSize: "20px", marginTop: "-4px" }}>＋</div>
                )}
                {option.price && (
                  <div style={{ color: "#28a745", fontSize: "12px" }}>
                    +${parseFloat(option.price).toFixed(2)}
                  </div>
                )}
                {option._isLocal && (
                  <div
                    style={{ fontSize: "10px", color: "#aaa", marginTop: "2px", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSavedOption(option.id);
                    }}
                  >
                    ✕ remove
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showBack && (
        <div className="mt-3 d-flex gap-2 align-items-center">
          <Button size="sm" color="secondary" outline onClick={onBack}>
            ← Back
          </Button>
          <Button size="sm" color="success" onClick={onDone}>
            Done ✓
          </Button>
        </div>
      )}

      <Modal
        isOpen={otherModal}
        toggle={() => {
          setOtherModal(false);
          setSaveMode(false);
        }}
        centered
        onOpened={() => document.getElementById("other-name-input")?.focus()}
      >
        <ModalHeader
          toggle={() => {
            setOtherModal(false);
            setSaveMode(false);
          }}
        >
          {saveMode ? `Save Custom ${addon.title}` : `Custom ${addon.title}`}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Name</Label>
            <Input
              id="other-name-input"
              value={otherName}
              onChange={(e) => setOtherName(e.target.value)}
              placeholder="Enter name"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  document.getElementById("other-price-input")?.focus();
                }
              }}
            />
          </FormGroup>
          <FormGroup>
            <Label>Price</Label>
            <Input
              id="other-price-input"
              type="number"
              step="0.01"
              value={otherPrice}
              onChange={(e) => setOtherPrice(e.target.value)}
              placeholder="0.00"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleOtherSubmit();
                }
              }}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleOtherSubmit} disabled={saving}>
            {saving ? "Saving…" : saveMode ? "Save" : "Add"}
          </Button>
          <Button color="secondary" onClick={() => setOtherModal(false)} disabled={saving}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AddonSelector;
